import { useEffect } from "react"

const SLIDE = "li.react-multi-carousel-item"
const FOCUSABLE = "a[href],button,input,select,textarea,[tabindex]"

/** react-multi-carousel marks off-screen slides aria-hidden but leaves their links/buttons tabbable. */
function syncSlide(slide: Element) {
  const hidden = slide.getAttribute("aria-hidden") === "true"
  slide.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((el) => {
    if (hidden) {
      if (el.dataset.carouselTabindex === undefined) {
        el.dataset.carouselTabindex = el.getAttribute("tabindex") ?? ""
        el.tabIndex = -1
      }
    } else if (el.dataset.carouselTabindex !== undefined) {
      const prev = el.dataset.carouselTabindex
      delete el.dataset.carouselTabindex
      if (prev === "") el.removeAttribute("tabindex")
      else el.setAttribute("tabindex", prev)
    }
  })
}

/**
 * Takes focusable content of aria-hidden carousel slides out of the tab order (it stays clickable).
 * Mount once, app-wide.
 */
export function useCarouselHiddenSlides() {
  useEffect(() => {
    const pending = new Set<Element>()
    let frame = 0

    const queue = (node: Node) => {
      if (!(node instanceof Element)) return
      const slide = node.closest(SLIDE)
      if (slide) pending.add(slide)
      else node.querySelectorAll(SLIDE).forEach((s) => pending.add(s))
    }

    const observer = new MutationObserver((records) => {
      records.forEach((r) => {
        if (r.type === "attributes") queue(r.target)
        else r.addedNodes.forEach(queue)
      })
      if (pending.size && !frame) {
        frame = requestAnimationFrame(() => {
          frame = 0
          pending.forEach(syncSlide)
          pending.clear()
        })
      }
    })

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-hidden"],
    })
    queue(document.body)
    pending.forEach(syncSlide)
    pending.clear()

    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [])
}
